import { makeStyles } from '@mui/styles';
import React, { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import JoyRide, { ACTIONS, CallBackProps, STATUS } from 'react-joyride';
import {
  Grid2 as Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { Configurator } from '../types/Configurator';
import { unknownGroup } from '../constants/Group';
import { ConfiguratorAppBar } from './ConfiguratorAppBar';
import { SitesBasicInformation } from './SitesBasicInformation';
import { SitesConfiguratorSettings } from './SitesConfiguratorSettings';
import { SitesColorsAndScene } from './SitesColorsAndScene';
import { SitesEmailAndContact } from './SitesEmailAndContact';
import { SitesLogosAndWatermarks } from './SitesLogosAndWatermarks';
import { SitesInformationalPanel } from './SitesInformationalPanel';
import { SitesPricingInformation } from './SitesPricingInformation';
import { SitesFloorplanOptions } from './SitesFloorplanOptions';
import { SitesAdvancedSection } from './SitesAdvancedSection';
import { SitesPromotionPanel } from './SitesPromotionPanel';
import { setSearchHidden } from '../ducks/search';
import { isIdeaRoomUser } from '../utils/userUtils';
import { unknownUser } from '../types/User';
import { I18nKeys } from '../constants/I18nKeys';
import { MaximumDigits } from '../constants/PricingSurcharge';
import { IdeaRoomOnlyIndicator } from './IdeaRoomOnlyIndicator';
import { useAppDispatch, useAppSelector } from '../hooks';
import { AppState } from '../types/AppState';
import { useClientDataRepo } from '../hooks/useClientDataRepo';
import { SITE_DETAIL_TABLE, SiteDetailChange } from '../constants/ClientData';
import { setClientDataType, setClientId, setSelectedTable } from '../ducks/clientDataSlice';
import { ClientDataType } from '../constants/ClientDataType';
import { SiteDetailPublishResultDialog } from './SiteDetailPublishResultDialog';
import { EmailDomainDialog } from './EmailDomainDialog';
import { isNotSiteDetailBranch } from '../utils/vendorDataUtils';
import { saveUserPreferences } from '../ducks/currentUserSlice';
import { UserPreference } from '../constants/User';
import { updateSiteDetails } from '../middleware/siteDetailsThunk';
import { useSharedDeleteBranchMutation } from '../services/clientDataApi';
import { setRunSitesTour } from '../ducks/gettingStartedSlice';
import { sitesTourMap } from '../constants/gettingStartedConfig';
import { JoyrideTooltip } from './JoyrideTooltip';

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  rootGrid: {
    flex: 1,
  },
  accordion: {
    width: '100%',
    '&$expanded': {
      margin: '0',
    },
  },
  sectionTitle: {
    marginLeft: '8px',
    fontWeight: 'bold',
  },
  accordionSummary: {
    '&$expanded': {
      margin: '20px 0 0 0',
    },
  },
  expanded: {},
  accordionDetails: {
    paddingTop: 0,
  },
});

export const Sites: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<any>({});

  const { clientId, clientDataType, selectedTable, isCreatingBranch } = useAppSelector(
    (state: AppState) => state?.clientData,
  );
  const [, { isLoading: isDeletingBranch }] = useSharedDeleteBranchMutation();

  if (clientDataType !== ClientDataType.Vendor) {
    dispatch(setClientDataType(ClientDataType.Vendor));
  }

  const runSitesTour = useAppSelector((state) => state?.gettingStarted.runSitesTour);
  const sitesTourKey = useAppSelector((state) => state?.gettingStarted.sitesTourKey);
  const [componentIsRunningSitesTour, setComponentIsRunningSitesTour] = useState(runSitesTour);

  const selectedViewerId = useAppSelector(
    (state: AppState) => state?.viewer?.selectedTabId || state?.viewer?.selectedClientId,
  );
  const vendorData = useAppSelector((state: AppState) => state?.vendorData.vendorData);
  const { isInitializingSelectedTableData, isUninitializedSelectedTableData, selectedTableData } = useClientDataRepo({
    useSelectedTableData: true,
    skipClientDataFetch: isNotSiteDetailBranch,
  });

  const {
    group: { configurators: groupConfigs = [], groupId: selectedGroupId } = unknownGroup,
    preferences: {
      [UserPreference.SitesPreferences]: { sectionsState: savedSectionsState = {}, ...sitesProfilePreferences } = {},
    } = {},
  } = useAppSelector((state) => state.currentUser);
  const { user } = useAppSelector((state) => state.currentUser || unknownUser);
  const [selectedConfigurator, setConfigurator] = useState<Configurator | undefined>(
    groupConfigs.find((c) => c.clientId === selectedViewerId) || groupConfigs[0],
  );
  const [sectionsState, setSectionsState] = useState<{
    [section: string]: boolean;
  }>(savedSectionsState);

  const onSectionChange = (section: string): void => {
    // If we're running the Getting Started Sites tour,
    //  we need to turn it off if the user attempts to change a section.
    // Right now, the tour (if running) forces a specific section to be open--so that we can show the user where/how to set Site colors or logo for example.
    // However, if the user wants to view other sections, we need to let them, so turn the tour off and revert back to respecting the settings stored in sectionsState
    if (componentIsRunningSitesTour) {
      setComponentIsRunningSitesTour(false);
    }

    setSectionsState((prevSectionsState) => {
      const newSectionsState = {
        ...prevSectionsState,
        [section]: !prevSectionsState?.[section],
      };
      dispatch(
        saveUserPreferences({
          userPreference: UserPreference.SitesPreferences,
          preferences: {
            sectionsState: newSectionsState,
            ...sitesProfilePreferences,
          },
        }),
      );
      return newSectionsState;
    });
  };

  if (selectedViewerId && selectedViewerId !== clientId) {
    dispatch(setClientId(selectedViewerId));
  }

  useLayoutEffect(() => {
    dispatch(setSearchHidden(true));
  });

  useEffect(() => {
    if (selectedTableData) {
      const { vendorData: { vendor: configVendorData = {} } = {} } = selectedConfigurator || {};
      // There should only be one row of data per client
      // Combine the table data with data from vendorData
      setTableData({ ...configVendorData, ...vendorData, ...selectedTableData[0] });
    }
  }, [selectedTableData, vendorData, selectedConfigurator]);

  if (selectedTable !== SITE_DETAIL_TABLE) {
    dispatch(setSelectedTable(SITE_DETAIL_TABLE));
  }

  useEffect(() => {
    if (isInitializingSelectedTableData || isUninitializedSelectedTableData || isCreatingBranch || isDeletingBranch) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isInitializingSelectedTableData, isUninitializedSelectedTableData, isCreatingBranch, isDeletingBranch]);

  const onChange = (changes: SiteDetailChange[] = []): void => {
    setTableData((prevTableData: any) =>
      changes.reduce((updatedTableData, { value: val, column: col, percentage: percent }) => {
        let columnValue = val;
        if (columnValue !== undefined && columnValue !== '' && percent && typeof val === 'string') {
          const valueAsString =
            val.length > MaximumDigits.PercentChange ? val.slice(0, MaximumDigits.PercentChange) : val;
          columnValue = (parseFloat(valueAsString) / 100).toString();
        }
        return { ...updatedTableData, [col]: columnValue };
      }, prevTableData),
    );
    dispatch(updateSiteDetails(changes));
  };

  const setClientAndConfigurator = (newClientId: string): void => {
    if (selectedViewerId !== newClientId) {
      setIsLoading(true);
      dispatch(setClientId(newClientId));
      setConfigurator(groupConfigs.find((c) => c.clientId === newClientId) || groupConfigs[0]);
    }
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, status } = data;
    if (action === ACTIONS.CLOSE || status === STATUS.SKIPPED || status === STATUS.FINISHED) {
      if (status === STATUS.FINISHED) {
        dispatch(setRunSitesTour(false));
      }
    }
  };

  const sections: { key: string; title: string; component: ReactNode; hidden: boolean; ideaRoomOnly?: boolean }[] =
    tableData && [
      {
        key: 'basicInformation',
        title: t(I18nKeys.SitesSectionBasicInformation),
        component: (
          <SitesBasicInformation
            data={tableData}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
        hidden: false,
      },
      {
        key: 'configuratorSettings',
        title: t(I18nKeys.SitesSectionConfiguratorSettings),
        component: (
          <SitesConfiguratorSettings
            data={tableData}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
        hidden: false,
      },
      {
        key: 'emailAndContact',
        title: t(I18nKeys.SitesSectionEmailAndContact),
        component: (
          <SitesEmailAndContact
            data={tableData}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
        hidden: false,
      },
      {
        key: 'logosAndWatermarks',
        title: t(I18nKeys.SitesSectionLogos),
        component: (
          <SitesLogosAndWatermarks
            data={tableData}
            clientId={clientId}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
        hidden: false,
      },
      {
        key: 'colorsAndScene',
        title: t(I18nKeys.SitesSectionColorsAndScene),
        component: (
          <SitesColorsAndScene
            data={tableData}
            clientId={clientId}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
        hidden: false,
      },
      {
        key: 'informationalPanel',
        title: t(I18nKeys.SitesSectionInformationPanel),
        component: (
          <SitesInformationalPanel
            data={tableData}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
        hidden: false,
      },
      {
        key: 'promotionalPanel',
        title: t(I18nKeys.SitesSectionLeadsPopupPanel),
        component: (
          <SitesPromotionPanel
            data={tableData}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
        hidden: false,
      },
      {
        key: 'sitePricingInformation',
        title: t(I18nKeys.SitesSectionPricingInformation),
        component: (
          <SitesPricingInformation
            data={tableData}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
      },
      {
        key: 'floorplans',
        title: t(I18nKeys.SitesSectionFloorplans),
        component: (
          <SitesFloorplanOptions
            data={tableData}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
      },
      {
        key: 'siteAdvancedSection',
        title: t(I18nKeys.SitesSectionAdvanced),
        component: (
          <SitesAdvancedSection
            data={tableData}
            onChange={onChange}
            isIdeaRoomUser={isIdeaRoomUser(user)}
            selectedGroupId={selectedGroupId}
          />
        ),
        hidden: false,
        ideaRoomOnly: false,
      },
    ];

  return (
    <div className={classes.root}>
      <ConfiguratorAppBar
        enabledOnProperty="clientId"
        isVendorProperty={false}
        configurators={groupConfigs}
        onTabChange={setClientAndConfigurator}
      />
      {!isLoading && (
        <Grid
          className={classes.rootGrid}
          container
          direction="column"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          {(!selectedConfigurator || selectedConfigurator) &&
            sections &&
            sections
              .filter((section) => !section.hidden)
              .map((section, i) => (
                <React.Fragment key={section.key}>
                  <Accordion
                    id={section.key}
                    defaultExpanded
                    classes={{ root: classes.accordion, expanded: classes.expanded }}
                    expanded={
                      componentIsRunningSitesTour
                        ? section.key === sitesTourMap[sitesTourKey]?.sectionKey
                        : !!sectionsState?.[section.key]
                    }
                    onChange={() => onSectionChange(section.key)}
                  >
                    <AccordionSummary
                      classes={{ content: classes.accordionSummary, expanded: classes.expanded }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      {section.ideaRoomOnly && <IdeaRoomOnlyIndicator />}
                      <Typography className={classes.sectionTitle} variant="overline">
                        {section.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>{section.component}</AccordionDetails>
                  </Accordion>
                  {/* Only add divider if not last section */}
                  {i < sections.length - 1 && <Divider />}
                </React.Fragment>
              ))}
          <JoyRide
            disableScrolling
            hideBackButton
            hideCloseButton
            run={runSitesTour}
            scrollOffset={200}
            steps={sitesTourMap[sitesTourKey]?.tourSteps}
            tooltipComponent={JoyrideTooltip}
            callback={handleJoyrideCallback}
            locale={{
              close: 'Got it',
            }}
            styles={{
              options: {
                zIndex: 1300,
              },
            }}
          />
        </Grid>
      )}
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', height: '100%' }}>
          <CircularProgress style={{ alignSelf: 'center' }} color="primary" />
        </div>
      )}
      <SiteDetailPublishResultDialog />
      <EmailDomainDialog />
    </div>
  );
};
